/**
 * src/views/CategoriesView.tsx
 * Ultra-Premium Apple iOS 18 Category Master Management Screen
 */

import React, { useState, useMemo } from 'react';
import { type CategoryItem } from '../services/api';
import { CategoryFormModal } from '../components/categories/CategoryFormModal';
import { GlassModal } from '../components/ui/GlassModal';
import { renderCategoryIcon } from '../utils/categoryIcons';
import { Plus, Search, Edit3, Trash2, Tag, Layers, Flame, AlertCircle } from 'lucide-react';
import { useToast } from '../context/ToastContext';

interface CategoriesViewProps {
  categories: CategoryItem[];
  onRefresh: () => Promise<void>;
  onCategoryCreated: (data: { name: string; icon: string; color: string }) => Promise<void>;
  onCategoryUpdated: (id: number, data: { name?: string; icon?: string; color?: string }) => Promise<void>;
  onCategoryDeleted: (id: number) => Promise<void>;
}

export const CategoriesView: React.FC<CategoriesViewProps> = ({
  categories,
  onRefresh,
  onCategoryCreated,
  onCategoryUpdated,
  onCategoryDeleted
}) => {
  const { success, error: toastError } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);

  // Delete confirmation
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categories;
    const term = searchTerm.toLowerCase();
    return categories.filter(c => c.name.toLowerCase().includes(term));
  }, [categories, searchTerm]);

  // Insights
  const stats = useMemo(() => {
    const total = categories.length;
    const defaultCount = categories.filter(c => c.is_default).length;
    const customCount = total - defaultCount;

    // Find highest transaction count
    let topCategory: CategoryItem | null = null;
    let maxCount = -1;
    for (const cat of categories) {
      const count = cat.transaction_count || 0;
      if (count > maxCount) {
        maxCount = count;
        topCategory = cat;
      }
    }

    return { total, defaultCount, customCount, topCategory, maxCount };
  }, [categories]);

  const handleOpenCreate = () => {
    setEditingCategory(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (cat: CategoryItem) => {
    setEditingCategory(cat);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: { name: string; icon: string; color: string }) => {
    try {
      if (editingCategory) {
        await onCategoryUpdated(editingCategory.id, data);
        success(`Category "${data.name}" updated!`, 'Saved');
      } else {
        await onCategoryCreated(data);
        success(`New category "${data.name}" created!`, 'Created');
      }
      await onRefresh();
    } catch (err: any) {
      toastError(err.message || 'Failed to save category');
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setIsDeleting(true);
      await onCategoryDeleted(categoryToDelete.id);
      success(`Category "${categoryToDelete.name}" removed`, 'Deleted');
      setCategoryToDelete(null);
      await onRefresh();
    } catch (err: any) {
      toastError(err.message || 'Failed to delete category');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container" style={{ paddingBottom: '90px', paddingTop: '20px' }}>
      {/* iOS Header with Primary Action Pill */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '24px'
        }}
      >
        <div>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'var(--ios-blue)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Tag size={13} />
            <span>Master Management</span>
          </div>
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 800,
              letterSpacing: '-0.04em',
              color: 'var(--text-primary)',
              margin: 0
            }}
          >
            Category Master
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0 0' }}>
            Customize spending classifications, squircle symbols, and color accents.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenCreate}
          className="ios-primary-button ios-tap-target"
          style={{
            padding: '12px 20px',
            fontSize: '0.9rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={18} strokeWidth={2.5} />
          <span>New Category</span>
        </button>
      </div>

      {/* iOS Health/Fitness Style Metrics Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '14px',
          marginBottom: '24px'
        }}
      >
        {/* Card 1: Total Master Categories */}
        <div
          className="ios-inset-group"
          style={{
            padding: '18px 20px',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div
            className="ios-icon-squircle blue"
            style={{ width: '44px', height: '44px', borderRadius: '12px' }}
          >
            <Layers size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total Categories
            </div>
            <div style={{ fontSize: '1.45rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              {stats.total}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
              {stats.defaultCount} Core · {stats.customCount} Custom
            </div>
          </div>
        </div>

        {/* Card 2: Highest Transaction Volume */}
        <div
          className="ios-inset-group"
          style={{
            padding: '18px 20px',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div
            className="ios-icon-squircle orange"
            style={{ width: '44px', height: '44px', borderRadius: '12px' }}
          >
            <Flame size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Highest Volume
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginTop: '2px' }}>
              {stats.topCategory?.name || 'N/A'}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--ios-orange)', fontWeight: 600 }}>
              {stats.maxCount > 0 ? `${stats.maxCount} historical transactions` : '0 transactions'}
            </div>
          </div>
        </div>
      </div>

      {/* iOS Search Bar */}
      <div
        className="ios-inset-group"
        style={{
          margin: '0 0 20px 0',
          padding: '8px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}
      >
        <Search size={18} color="var(--text-tertiary)" />
        <input
          type="text"
          placeholder="Filter categories by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text-primary)',
            fontSize: '0.92rem',
            fontFamily: 'inherit'
          }}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Categories Inset Grouped List */}
      <div style={{ marginBottom: '8px' }}>
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-tertiary)',
            paddingLeft: '14px',
            marginBottom: '8px'
          }}
        >
          Categories ({filteredCategories.length})
        </div>

        <div className="ios-inset-group" style={{ margin: 0 }}>
          {filteredCategories.length === 0 ? (
            <div style={{ padding: '36px 20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
              No categories match "{searchTerm}"
            </div>
          ) : (
            filteredCategories.map((cat, idx) => {
              const count = cat.transaction_count || 0;
              const catColor = cat.color || '#0a84ff';

              return (
                <div
                  key={cat.id}
                  className="ios-list-row"
                  style={{
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: idx < filteredCategories.length - 1 ? 'var(--border-hairline)' : 'none'
                  }}
                >
                  {/* Left: Squircle & Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      className="ios-icon-squircle"
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '13px',
                        backgroundColor: `${catColor}22`,
                        color: catColor,
                        border: `1px solid ${catColor}35`,
                        boxShadow: `0 4px 12px ${catColor}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {renderCategoryIcon(cat.icon, 22, catColor)}
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {cat.name}
                        </span>
                        {cat.is_default === 1 && (
                          <span
                            style={{
                              fontSize: '0.625rem',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              padding: '2px 6px',
                              borderRadius: 'var(--radius-pill)',
                              background: 'rgba(255, 255, 255, 0.08)',
                              color: 'var(--text-tertiary)'
                            }}
                          >
                            Core
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: count > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                          {count.toLocaleString()}
                        </span>{' '}
                        transactions logged
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => handleOpenEdit(cat)}
                      className="ios-tap-target"
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        border: '1px solid var(--border-subtle)',
                        background: 'rgba(255, 255, 255, 0.04)',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      title="Edit Category"
                    >
                      <Edit3 size={16} />
                    </button>

                    <button
                      type="button"
                      onClick={() => setCategoryToDelete(cat)}
                      className="ios-tap-target"
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 69, 58, 0.25)',
                        background: 'rgba(255, 69, 58, 0.08)',
                        color: 'var(--ios-red)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      title="Delete Category"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Category Create/Edit Modal */}
      <CategoryFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        initialCategory={editingCategory}
      />

      {/* Delete Confirmation Sheet */}
      {categoryToDelete && (
        <GlassModal
          isOpen={Boolean(categoryToDelete)}
          onClose={() => setCategoryToDelete(null)}
          title="Remove Category"
          maxWidth="440px"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div
              style={{
                padding: '16px',
                borderRadius: '16px',
                background: 'rgba(255, 69, 58, 0.1)',
                border: '1px solid rgba(255, 69, 58, 0.25)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}
            >
              <AlertCircle size={22} color="var(--ios-red)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--ios-red)' }}>
                  Are you sure you want to delete "{categoryToDelete.name}"?
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '4px', lineHeight: 1.45 }}>
                  {categoryToDelete.transaction_count && categoryToDelete.transaction_count > 0 ? (
                    <>
                      This category is currently attached to{' '}
                      <strong>{categoryToDelete.transaction_count} transaction(s)</strong>.
                      Deleting it will unlink the category ID while keeping amounts and financial records 100% intact.
                    </>
                  ) : (
                    <>This category is not used by any transactions and can be safely removed.</>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setCategoryToDelete(null)}
                className="ios-tap-target"
                style={{
                  flex: 1,
                  padding: '14px',
                  borderRadius: 'var(--radius-pill)',
                  border: 'var(--border-hairline)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  fontSize: '0.92rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Keep Category
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="ios-tap-target"
                style={{
                  flex: 1,
                  padding: '14px',
                  borderRadius: 'var(--radius-pill)',
                  border: 'none',
                  background: 'var(--ios-red)',
                  color: '#ffffff',
                  fontSize: '0.92rem',
                  fontWeight: 700,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 16px rgba(255, 69, 58, 0.35)'
                }}
              >
                {isDeleting ? 'Removing...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </GlassModal>
      )}
    </div>
  );
};
